/**
 * ⌘K palette: every destination in both workspaces, and every record an
 * operator looks for by name — organizations, accounts, the current tenant's
 * agents, plugins, partners, staff — one keystroke away.
 *
 * A destination navigates. A record opens in the record panel on the page
 * the operator is on, so finding something never costs them their place
 * (Feature 70, §5). Record groups appear once the query is long enough to
 * mean something and keep their best few; an empty palette is the
 * destinations alone, and a pasted task id is a row of its own, first. The
 * palette does its own matching and ordering
 * (`palette-search.ts`), so cmdk keeps only the list, the selection, and the
 * arrow keys. The CommandDialog from the ui layer keeps the trap and the
 * restore and draws finixt-ui's panel.
 *
 * The lists are fetched only while the palette is open, so the cost lands on
 * the operator who asked for it rather than on every page load.
 */
import { listAccounts } from '@optum/api-client/endpoints/accounts';
import { listAgents } from '@optum/api-client/endpoints/agents';
import { listCatalog } from '@optum/api-client/endpoints/catalog';
import { listFederatedPartners } from '@optum/api-client/endpoints/federated-partners';
import { listOrganizations } from '@optum/api-client/endpoints/organizations';
import { listPlatformStaff } from '@optum/api-client/endpoints/platform-staff';
import type { CatalogKind } from '@optum/api-client/types';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Command } from 'cmdk';
import { useEffect, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';

import { qk } from '../../lib/query-keys.js';
import { useCurrentOrgId } from '../../lib/use-current-org.js';
import { CommandDialog, PaletteRow } from '../../ui/CommandDialog.js';
import { Icon } from '../../ui/finixt.js';
import { pluginId } from '../../features/plugins/plugin-id.js';
import { orgNav, PLATFORM_NAV } from './nav-config.js';
import {
  GROUP_LIMIT,
  paletteMatches,
  searchesRecords,
  taskIdIn,
  type PaletteDestination,
  type PaletteEntry,
  type PaletteRecord,
} from './palette-search.js';
import { type RecordRef, useRecordPanel } from './record.js';

const HINT_STYLE: CSSProperties = {
  fontSize: 'var(--text-label-lg)',
  color: 'var(--text-ghost)',
};

const STALE = 60_000;
const CATALOG_KINDS: readonly CatalogKind[] = ['capabilities', 'tools'];
const KIND_WORD: Record<CatalogKind, string> = {
  capabilities: 'capability',
  tools: 'tool',
};

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface RecordGroup {
  heading: string;
  rows: PaletteRecord[];
}

/** Every record the palette can find, by group, from the lists it has loaded. */
function useRecordGroups(open: boolean, orgId: string | null): RecordGroup[] {
  const organizations = useQuery({
    queryKey: qk.orgs(),
    queryFn: () => listOrganizations(),
    enabled: open,
    staleTime: STALE,
  });
  const accounts = useQuery({
    queryKey: qk.accounts(),
    queryFn: () => listAccounts(),
    enabled: open,
    staleTime: STALE,
  });
  const agents = useQuery({
    queryKey: qk.agents(orgId ?? ''),
    queryFn: () => listAgents({ organization_id: orgId ?? '' }),
    enabled: open && orgId !== null,
    staleTime: STALE,
  });
  const catalog = useQueries({
    queries: CATALOG_KINDS.map((kind) => ({
      queryKey: qk.catalogList(kind),
      queryFn: () => listCatalog(kind),
      enabled: open,
      staleTime: STALE,
    })),
  });
  const partners = useQuery({
    queryKey: qk.federatedPartners(),
    queryFn: () => listFederatedPartners(),
    enabled: open,
    staleTime: STALE,
  });
  const staff = useQuery({
    queryKey: qk.platformStaff(),
    queryFn: () => listPlatformStaff(),
    enabled: open,
    staleTime: STALE,
  });

  // Plain maps over lists already in memory; cheap enough to rebuild per render.
  const catalogData = catalog.map((q) => q.data);
  return [
    {
      heading: 'Organizations',
      rows: (organizations.data ?? []).map((o) => ({
        key: `organization:${o.id}`,
        ref: { kind: 'organization', id: o.id },
        icon: 'apartment',
        label: o.name,
        hint: o.status,
        fields: [o.id],
      })),
    },
    {
      heading: 'Accounts',
      rows: (accounts.data ?? []).map((a) => ({
        key: `account:${a.external_sub}`,
        ref: { kind: 'account', id: a.external_sub },
        icon: 'person',
        label: a.email ?? a.external_sub,
        hint: a.status,
        fields: [a.external_sub, a.platform_user_id],
      })),
    },
    ...(orgId
      ? [
          {
            heading: 'Agents in this organization',
            rows: (agents.data ?? []).map<PaletteRecord>((a) => ({
              key: `agent:${a.id}`,
              ref: { kind: 'agent', id: a.id, orgId },
              icon: 'smart_toy',
              label: a.name,
              hint: a.runtime_state,
              fields: [a.id],
            })),
          },
        ]
      : []),
    {
      heading: 'Plugins',
      rows: CATALOG_KINDS.flatMap((kind, i) =>
        (catalogData[i] ?? []).map<PaletteRecord>((e) => ({
          key: `plugin:${kind}/${e.name}`,
          ref: { kind: 'plugin', id: pluginId(kind, e.name) },
          icon: 'extension',
          label: e.name,
          hint: e.installed_version
            ? `${KIND_WORD[kind]} · ${e.installed_version}`
            : KIND_WORD[kind],
        })),
      ),
    },
    {
      heading: 'Partners',
      rows: (partners.data ?? []).map((p) => ({
        key: `partner:${p.id}`,
        ref: { kind: 'partner', id: p.id },
        icon: 'hub',
        label: p.name,
        hint: p.status,
        fields: [p.idp_name, p.id],
      })),
    },
    {
      heading: 'Staff',
      rows: (staff.data ?? []).map((s) => ({
        key: `staff:${s.id}`,
        ref: { kind: 'staff', id: s.id },
        icon: 'badge',
        label: s.email,
        hint: s.role.replace('optum-platform-', ''),
        fields: [s.id],
      })),
    },
  ];
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const panel = useRecordPanel();
  const orgId = useCurrentOrgId();
  const [query, setQuery] = useState('');

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        onOpenChange(!open);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onOpenChange]);

  // A fresh palette starts empty, whatever was typed into the last one.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const groups = useRecordGroups(open, orgId);

  function go(to: string) {
    onOpenChange(false);
    navigate(to);
  }

  function openRecord(ref: RecordRef) {
    onOpenChange(false);
    panel.open(ref);
  }

  // The tenant nav is only reachable from inside a tenant, so surface it here
  // when there is one; otherwise the palette can only ever offer half the app.
  const platform: PaletteDestination[] = PLATFORM_NAV.flatMap((g) => g.items).map((item) => ({
    key: `go:${item.to}`,
    to: item.to,
    icon: item.icon,
    label: item.label,
    hint: item.to,
  }));
  const scoped: PaletteDestination[] = (orgId ? orgNav(orgId) : [])
    .flatMap((g) => g.items)
    .map((item) => ({
      key: `go:${item.to}`,
      to: item.to,
      icon: item.icon,
      label: item.label,
      hint: 'this tenant',
    }));
  // A whole task id opens that task; a prefix would need the server search.
  const taskId = taskIdIn(query);
  const platformRows = paletteMatches(platform, query);
  const scopedRows = paletteMatches(scoped, query);
  const recordGroups = searchesRecords(query)
    ? groups
        .map((g) => ({ heading: g.heading, rows: paletteMatches(g.rows, query, GROUP_LIMIT) }))
        .filter((g) => g.rows.length > 0)
    : [];

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} label="Command palette">
      <Command
        label="Command palette"
        shouldFilter={false}
        style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '13px 14px',
            borderBottom: '1px solid var(--border-divider)',
          }}
        >
          <Icon name="search" size={18} color="var(--text-faint)" />
          <Command.Input
            value={query}
            onValueChange={setQuery}
            placeholder="Search pages and records…"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-body-lg)',
              color: 'var(--text-strong)',
            }}
          />
          <span
            style={{
              ...HINT_STYLE,
              border: '1px solid var(--border-strong)',
              borderRadius: 4,
              padding: '1px 5px',
            }}
            className="mono"
          >
            ESC
          </span>
        </div>

        <Command.List style={{ overflowY: 'auto', padding: 5, minHeight: 0 }}>
          <Command.Empty
            style={{
              padding: '26px 11px',
              textAlign: 'center',
              fontSize: 'var(--text-body-sm)',
              color: 'var(--text-faint)',
            }}
          >
            No matches
          </Command.Empty>

          {taskId ? (
            <Command.Group heading="Task">
              <PaletteItem
                entry={{
                  key: `task:${taskId}`,
                  icon: 'task_alt',
                  label: `Open task ${taskId.slice(0, 8)}…`,
                  hint: 'by id',
                }}
                onSelect={() => openRecord({ kind: 'task', id: taskId })}
              />
            </Command.Group>
          ) : null}

          {platformRows.length > 0 ? (
            <Command.Group heading="Platform">
              {platformRows.map((d) => (
                <PaletteItem key={d.key} entry={d} onSelect={() => go(d.to)} />
              ))}
            </Command.Group>
          ) : null}

          {scopedRows.length > 0 ? (
            <Command.Group heading="This organization">
              {scopedRows.map((d) => (
                <PaletteItem key={d.key} entry={d} onSelect={() => go(d.to)} />
              ))}
            </Command.Group>
          ) : null}

          {recordGroups.map((g) => (
            <Command.Group key={g.heading} heading={g.heading}>
              {g.rows.map((r) => (
                <PaletteItem key={r.key} entry={r} onSelect={() => openRecord(r.ref)} />
              ))}
            </Command.Group>
          ))}
        </Command.List>

        <div
          style={{
            display: 'flex',
            gap: 14,
            padding: '9px 14px',
            borderTop: '1px solid var(--border-divider)',
            background: 'var(--surface-sunken)',
            ...HINT_STYLE,
          }}
          className="mono"
        >
          <span>↑↓ NAVIGATE</span>
          <span>↵ OPEN</span>
          <span>ESC CLOSE</span>
        </div>
      </Command>
    </CommandDialog>
  );
}

function PaletteItem({ entry, onSelect }: { entry: PaletteEntry; onSelect: () => void }) {
  return (
    <PaletteRow
      value={entry.key}
      onSelect={onSelect}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        padding: '7px 9px',
        borderRadius: 'var(--radius-control)',
        cursor: 'pointer',
      }}
    >
      <Icon name={entry.icon} size={17} color="var(--text-soft)" />
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 'var(--text-body-sm)',
          color: 'var(--text-strong)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {entry.label}
      </span>
      {entry.hint ? (
        <span
          style={{ flexShrink: 0, fontSize: 'var(--text-meta)', color: 'var(--text-faint)' }}
          className="mono"
        >
          {entry.hint}
        </span>
      ) : null}
    </PaletteRow>
  );
}
